import React from 'react';
import {
  User, BookMarked, ShieldCheck, FileText, Layers, AlertTriangle, CheckCircle2,
  Plus, Pencil, Flag, Link2, XCircle, Check, X, PlayCircle, FolderPlus, Lightbulb, History,
} from 'lucide-react';
import { AvatarActionType, AvatarRecommendedAction } from '../../../types/avatarActions';
import { TriggerTaskProgress } from './TriggerTaskProgress';

interface ActionConfigEntry {
  label: string;
  color: string;
  icon: React.ReactNode;
}

const ACTION_CONFIG: Record<AvatarActionType, ActionConfigEntry> = {
  create_persona:    { label: 'New persona',   color: 'border-indigo-200 text-indigo-700 hover:bg-indigo-50', icon: <User size={11} /> },
  create_user_story: { label: 'New story',     color: 'border-blue-200 text-blue-700 hover:bg-blue-50',       icon: <BookMarked size={11} /> },
  create_nfr:        { label: 'New NFR',       color: 'border-amber-200 text-amber-700 hover:bg-amber-50',    icon: <ShieldCheck size={11} /> },
  create_adr:        { label: 'New ADR',       color: 'border-purple-200 text-purple-700 hover:bg-purple-50', icon: <FileText size={11} /> },
  create_feature:    { label: 'New feature',   color: 'border-teal-200 text-teal-700 hover:bg-teal-50',       icon: <Layers size={11} /> },
  create_raid_item:  { label: 'New RAID item', color: 'border-red-200 text-red-700 hover:bg-red-50',          icon: <AlertTriangle size={11} /> },
  resolve_raid_item: { label: 'Resolve item',  color: 'border-green-200 text-green-700 hover:bg-green-50',    icon: <CheckCircle2 size={11} /> },
  add_ac:            { label: 'Add AC',        color: 'border-blue-200 text-blue-700 hover:bg-blue-50',       icon: <Plus size={11} /> },
  reword_story:      { label: 'Reword story',  color: 'border-purple-200 text-purple-700 hover:bg-purple-50', icon: <Pencil size={11} /> },
  set_priority:      { label: 'Set priority',  color: 'border-amber-200 text-amber-700 hover:bg-amber-50',    icon: <Flag size={11} /> },
  link_milestone:    { label: 'Link milestone', color: 'border-teal-200 text-teal-700 hover:bg-teal-50',      icon: <Link2 size={11} /> },
  link_raid:         { label: 'Link RAID item', color: 'border-red-200 text-red-700 hover:bg-red-50',         icon: <Link2 size={11} /> },
  trigger_task:      { label: 'Run task',       color: 'border-indigo-200 text-indigo-700 hover:bg-indigo-50', icon: <PlayCircle size={11} /> },
  suggest_story_group:          { label: 'Group stories',        color: 'border-cyan-200 text-cyan-700 hover:bg-cyan-50',       icon: <FolderPlus size={11} /> },
  suggest_alternative_approach: { label: 'Alternative approach', color: 'border-teal-200 text-teal-700 hover:bg-teal-50',       icon: <Lightbulb size={11} /> },
  flag_legacy_assumption:       { label: 'Challenge assumption', color: 'border-orange-200 text-orange-700 hover:bg-orange-50', icon: <History size={11} /> },
};

interface ActionButtonRowProps {
  actions: AvatarRecommendedAction[];
  onSelect: (action: AvatarRecommendedAction) => void;
  onDismiss: (action: AvatarRecommendedAction) => void;
  disabled?: boolean;
  sessionId?: string;
  onViewTaskDetail?: (workflowId: string, executionId: string) => void;
}

export function ActionButtonRow({ actions, onSelect, onDismiss, disabled, sessionId, onViewTaskDetail }: ActionButtonRowProps) {
  if (!actions.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      {actions.map(action => {
        const cfg = ACTION_CONFIG[action.type];
        if (!cfg) return null;
        const decided = action.state !== 'proposed';

        if (decided) {
          if (action.type === 'trigger_task' && action.state === 'accepted' && action.appliedEntityId) {
            return (
              <TriggerTaskProgress
                key={action.id}
                sessionId={sessionId}
                workflowId={action.appliedEntityId}
                executionId={action.appliedEntityId}
                label={action.label}
                onViewDetail={onViewTaskDetail}
              />
            );
          }
          return (
            <span
              key={action.id}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold border ${
                action.state === 'accepted'
                  ? 'border-green-200 text-green-600 bg-green-50/50'
                  : 'border-slate-200 text-slate-400 bg-slate-50 line-through'
              }`}
            >
              {action.state === 'accepted' ? <Check size={10} /> : <XCircle size={10} />}
              {action.label}
            </span>
          );
        }

        return (
          <span key={action.id} className="inline-flex items-center gap-0.5">
            <button
              type="button"
              disabled={disabled}
              title={action.rationale}
              onClick={() => onSelect(action)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold border transition-colors disabled:opacity-50 disabled:pointer-events-none ${cfg.color}`}
            >
              {cfg.icon}
              {action.label}
            </button>
            <button
              type="button"
              disabled={disabled}
              title="Dismiss"
              onClick={() => onDismiss(action)}
              className="p-1 rounded-lg text-slate-300 hover:text-slate-500 hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              <X size={11} />
            </button>
          </span>
        );
      })}
    </div>
  );
}
